import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import ReactDOM from 'react-dom';
import {
  Button,
  Modal,
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InlineLoading
} from 'carbon-components-react';
import {
  currentUser,
  selectScreenSize,
  selectBaseMap,
  selectorAlertNotificationShowHide
} from 'redux/session/selectors';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import {
  addAlert,
  removeAlert,
  fetchUserStart,
  updateUserStart
} from 'redux/session/actions';
import { disableTeamPicker, setTeamBoundaryToggle } from 'redux/team/actions';
import {
  selectCurrentTeamId,
  selectCurrentTeamShape,
  selectCurrentTeamCreateEnquiryDefaultValues,
  selectTeamBoundaryToggle
} from 'redux/team/selectors';
import { useSelector, useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { SCREEN_SIZE, AddressType, APP_URL } from 'types';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import MaskingLayer from 'components/Map/maskingLayer';
import MapTools from './components/map/tools';
import MapView from './components/map/view';
import ConfirmationPanel from './components/confirmationPanel';
import EnquiryFormPanel from './components/formPanel';
import EnquiryUploadPanel from './components/uploadPanel';
import LiveAreaTag from './components/liveAreaTag';
import ShowRecentsToggle from './components/showRecentsToggle';
import UserReferenceTooltip from './components/userReferenceTooltip';
import {
  addErrorShapes,
  addShapesReference,
  clearShapesReference,
  clearErrorShapes,
  highlightMapShape,
  showRecent,
  clearGraphic,
  getInstruction,
  refreshShowRecent,
  geocodeToAddress,
  encodeOpenLocationCode
} from './components/map/helper';
import { removePin, zoomToFeature } from 'components/Map/helper';
import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import {
  clearShape,
  clearLodgedEnquiry,
  clearDuplicateEnquiry,
  setMultipleDrawMode,
  setEnquiryAddress,
  getGeocodeStart,
  getEnquiryEssentialsStart,
  getDuplicateEnquiryStart,
  getLastEnquiryStart,
  getAssetOwnerCountStart,
  getAssetOwnerCountFailure,
  checkShapeWithinTeamStart,
  checkShapeWithinTeamFailure,
  getRestrictionStart,
  getDraftStart,
  discardDraftStart,
  saveDraftStart,
  setUseUploadedShapeUserReference
} from './localStore/actions';
import { LocalDispatchContext, LocalStoreContext } from './localStore/context';
import { EnquiryShape, LodgeEnqyiryInitialValues } from './localStore/types';
import { useEmptyCallback, useComponentMount } from 'utilities/hooks.util';
import Utils from 'utilities/helper.util';

const initialValues: LodgeEnqyiryInitialValues = {
  locations: [],
  jobStartDate: '',
  jobEndDate: '',
  userReference: '',
  notes: '',
  autoRenew: false
};

const recentPolygonSymbol = {
  type: 'simple-fill',
  style: 'solid',
  color: [0, 0, 0, 0.16],
  outline: {
    style: 'none'
  }
};

const recentDarkPolygonSymbol = {
  type: 'simple-fill',
  color: [0, 89, 254, 0.35],
  style: 'solid',
  outline: {
    color: [0, 169, 230, 0.75],
    width: 1
  }
};

const setRecentRenderer = (theme: string) => {
  const { loadedMap } = window;
  if (loadedMap) {
    const layer = loadedMap.findLayerById('recent-enquiries-layer');
    if (layer) {
      const renderer = {
        type: 'simple',
        symbol: theme === 'dark' ? recentDarkPolygonSymbol : recentPolygonSymbol
      };

      layer.renderer = renderer;
    }
  }
};

const setRecentDefinitionExpression = (
  userId: number,
  teamId?: number,
  selectedRecentDuration: number = 7
) => {
  const { loadedMap } = window;
  if (loadedMap) {
    const layer = loadedMap.findLayerById('recent-enquiries-layer');
    if (layer) {
      const daysBefore = Utils.getDateBeforeDays(selectedRecentDuration).endOf(
        'day'
      );
      const userOrTeamFilter = teamId
        ? `project_id = ${teamId}`
        : `user_id = ${userId}`;
      layer.definitionExpression = `enquiry_at > TIMESTAMP '${daysBefore
        .utc()
        .format('YYYY-MM-DD HH:hh:ss')}' AND ${userOrTeamFilter}`;
    }
  }
};

const addMaskingLayer = (shape: GeoJSON.Polygon, zoom: boolean) => {
  const { loadedMap, loadedMapModules } = window;
  if (loadedMap && loadedMapModules) {
    const { Graphic, WebMercatorUtils } = loadedMapModules;
    let maskingLayer = loadedMap.findLayerById('masking-layer');
    if (!maskingLayer) {
      maskingLayer = MaskingLayer({
        id: 'masking-layer',
        color: [0, 0, 0, 0.2],
        distance: 1
      });
      loadedMap.add(maskingLayer);
    }

    const geometry = geojsonToArcGIS(shape);
    const webMercatorGeometry =
      WebMercatorUtils.geographicToWebMercator(geometry);
    const graphic = new Graphic({
      geometry: {
        ...webMercatorGeometry,
        type: 'polygon'
      }
    });
    maskingLayer.geometry = graphic.geometry;
    if (zoom) {
      zoomToFeature(graphic.geometry, 0, true);
    }
  }
};

const LodgeEnquiry: React.FC = () => {
  let defaultShowRecentsDuration: any = Cookies.get(
    'default-show-recents-duration'
  );
  defaultShowRecentsDuration =
    defaultShowRecentsDuration && JSON.parse(defaultShowRecentsDuration);

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{ duplicateFrom?: string }>();
  const user = useSelector(currentUser);
  const screenSize = useSelector(selectScreenSize);
  const baseMap = useSelector(selectBaseMap);
  const alertNotificationToggle = useSelector(
    selectorAlertNotificationShowHide
  );
  const showTeamBoundary = useSelector(selectTeamBoundaryToggle);
  const teamId = useSelector(selectCurrentTeamId);
  const teamShape = useSelector(selectCurrentTeamShape);
  const teamDefaultValues = useSelector(
    selectCurrentTeamCreateEnquiryDefaultValues
  );
  const [inValidAccess, setInValidAccess] = useState<boolean>(false);
  const [isExternalInPrivate, setIsExternalInPrivate] =
    useState<boolean>(false);
  const [activeTool, setActiveTool] = useState<string>('selector');
  const [mapReady, setMapReady] = useState<boolean>(false);
  const [editingDrawShape, setEditingDrawShape] = useState<boolean>(false);
  const [activateEnquiryForm, setActivateEnquiryForm] =
    useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [showClearMapWarningModal, setShowClearMapWarningModal] =
    useState<boolean>(false);
  const [showTooltipPolygonUid, setShowTooltipPolygonUid] = useState<number>(0);
  const [showTooltipReferUid, setShowTooltipReferUid] = useState<number>(0);
  const [userRefTooltipMessage, setUserRefTooltipMessage] =
    useState<string>('');
  const [showUnsavedAlert, setShowUnsavedAlert] = useState<boolean>(false);
  const [showDraftModal, setShowDraftModal] = useState<boolean>(false);
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const {
    shapes: drawedShapes,
    geocodeResponse,
    geocodeLoading = false,
    duplicating = false,
    draft,
    fetchingDraft,
    discardingDraft,
    discardedDraft,
    lodgingNewEnquiry,
    enquiries,
    multipleDrawMode,
    bulkCreating,
    pendingEnquiriesAmount,
    shapeWithinTeam,
    assetOwnerNumLoading,
    authorityTypeList,
    authorityList
  } = localStore;
  const [recentVisible, setRecentVisible] = useState<boolean>(false);
  const [jobIdInNotes, setJobIdInNotes] = useState<string>('');
  const [selectedShapeInMap, setSelectedShapeInMap] = useState<number>();
  const [formInitialValues, setFormInitialValues] =
    useState<LodgeEnqyiryInitialValues>(initialValues);
  const [selectedRecentDuration, setSelectedRecentDuration] = useState<number>(
    defaultShowRecentsDuration?.id === user.id
      ? defaultShowRecentsDuration.duration
      : 7
  );
  const [showDrawingTooltip, setShowDrawingTooltip] = useState<boolean>(false);
  const [shapeChanged, setShapeChanged] = useState<boolean>(false);
  const [formChanged, setFormChanged] = useState<boolean>(false);
  const projectOrganisationId = useRef<number>();
  const userDefaultProject = useRef<number>();
  const formValuesRefs = useRef<LodgeEnqyiryInitialValues>();
  const addedMaskOnce = useRef<boolean>(false);
  const shapeAmount = useRef<number>(0);
  const pendingClearMapFlag = useRef<boolean>(false);
  const canSelectShape = useRef<boolean>(false);
  const canHoverShape = useRef<boolean>(false);
  const lodgedEnquiryOnce = useRef<boolean>(false);
  const shapeDetailFormTouched = useRef<boolean>(false);
  const shapesInitialed = useRef<boolean>(false);
  const duplicatedShapeMapId = useRef<number | undefined>();
  const shapeTeamId = useRef<number | undefined>();
  const appliedDraftToForm = useRef<boolean>(false);
  const baseMapTheme = baseMap?.theme;

  const onClickSelector = () => {
    const { loadedMap, loadedSketchViewModel } = window;

    if (loadedMap !== undefined && loadedSketchViewModel !== undefined) {
      const drawingLayer: __esri.GraphicsLayer =
        loadedMap.findLayerById('drawing-layer');
      if (drawingLayer) {
        if (drawingLayer.graphics.length === 0) {
          loadedSketchViewModel.cancel();
        } else {
          loadedSketchViewModel.complete();
        }
      } else {
        loadedSketchViewModel.delete();
      }
      setActiveTool('selector');
    }
  };

  const onClickDrawPolygon = (type: string, multiple: boolean) => {
    const { loadedSketchViewModel } = window;
    if (loadedSketchViewModel) {
      if (!multiple) {
        clearGraphic();
        dispatchClearShape();
      }
      loadedSketchViewModel.create(type, {
        mode: type !== 'circle' ? 'click' : 'freehand'
      });
      setActiveTool(type);
    }
  };

  const onClickClearDrawing = () => {
    clearGraphic();
    dispatchClearShape();
    setActiveTool('selector');
  };

  const onClickTools = (tool: string, multiple: boolean) => {
    removePin();
    switch (tool) {
      case 'selector': {
        onClickSelector();
        setShowDrawingTooltip(false);
        break;
      }
      case 'circle': {
        onClickDrawPolygon('circle', multiple);
        setShowDrawingTooltip(true);
        break;
      }
      case 'polyline': {
        onClickDrawPolygon('polyline', multiple);
        setShowDrawingTooltip(true);
        break;
      }
      case 'polygon': {
        onClickDrawPolygon('polygon', multiple);
        setShowDrawingTooltip(true);
        break;
      }
      case 'upload': {
        setActiveTool('upload');
        setShowUpload(true);
        setShowDrawingTooltip(false);
        break;
      }
      case 'remove': {
        onClickClearDrawing();
        setShowDrawingTooltip(false);
        break;
      }
    }
  };

  const onDeleteDrawedPolygon = () => {
    const { loadedSketchViewModel } = window;

    if (loadedSketchViewModel !== undefined) {
      loadedSketchViewModel.delete();
    }
  };

  const dispatchClearShape = useEmptyCallback(() => {
    localDispatch(clearShape());
  });

  const dispatchClearLodgedEnquiry = useEmptyCallback(() => {
    localDispatch(clearLodgedEnquiry());
  });

  const dispatchSetEnquiryAddress = useEmptyCallback(
    (address: AddressType, longitude: number, latitude: number) => {
      localDispatch(setEnquiryAddress({ address, longitude, latitude }));
    }
  );

  const dispatchGetDuplicateEnquiry = useEmptyCallback((id: string) => {
    localDispatch(getDuplicateEnquiryStart({ enquiryId: id }));
  });

  const dispatchClearDuplicateEnquiry = useEmptyCallback(() => {
    localDispatch(clearDuplicateEnquiry());
  });

  const dispatchGetLastEnquiry = useEmptyCallback(() => {
    localDispatch(getLastEnquiryStart());
  });

  const dispatchGetGeocodeStart = useEmptyCallback(
    (centroid: { longitude: number; latitude: number }) => {
      localDispatch(getGeocodeStart({ ...centroid }));
    }
  );

  const dispatchSetUseUploadedUserRef = useEmptyCallback((checked: boolean) => {
    localDispatch(setUseUploadedShapeUserReference(checked));
  });

  const dispatchGetAssetOwnerCount = useEmptyCallback(
    (shapes: any[], orgId?: number) => {
      if (shapes.length > 0) {
        localDispatch(
          getAssetOwnerCountStart({
            shapes: shapes.map((s) => s.geoJson),
            orgId
          })
        );
      } else {
        //reset assetowner count
        localDispatch(getAssetOwnerCountFailure());
      }
    }
  );

  const dispatchCheckShapeWithinTeam = useEmptyCallback(
    (teamId: number, shapes: any[]) => {
      localDispatch(
        checkShapeWithinTeamStart({
          teamId,
          shapes: shapes.map((s) => s.geoJson)
        })
      );
    }
  );

  const dispatchResetShapeWithinTeam = useEmptyCallback(() => {
    localDispatch(checkShapeWithinTeamFailure(''));
  });

  const dispatchSetMultipleDrawMode = useEmptyCallback((flag: boolean) => {
    localDispatch(setMultipleDrawMode(flag));
  });

  const dispatchGetDrart = useEmptyCallback(() => {
    localDispatch(getDraftStart());
  });

  const dispatchDiscardDrart = useEmptyCallback(() => {
    localDispatch(discardDraftStart());
  });

  const setDuplicatedShapeMapId = useEmptyCallback((id: number) => {
    duplicatedShapeMapId.current = id;
  });

  const debouncedDispatchSaveDraft = useEmptyCallback(
    Utils.debounce(
      (
        payload:
          | { details: any; teamId?: number }[]
          | { details: any; teamId?: number }[][]
      ) => {
        const unwrappedPayload = Array.isArray(payload[0])
          ? payload[0][0]
          : payload[0];

        localDispatch(saveDraftStart(unwrappedPayload));
      },
      1500
    )
  );

  const dispatchGetRestriction = useEmptyCallback(() => {
    localDispatch(getRestrictionStart());
  });

  const cancelEnquiryHandler = (clearValues: boolean) => {
    if (clearValues) {
      formValuesRefs.current = undefined;
      appliedDraftToForm.current = false;
    }
    onClickClearDrawing();
    dispatchClearLodgedEnquiry();
    dispatchClearDuplicateEnquiry();
    setActivateEnquiryForm(false);
    setShowConfirm(false);
    dispatchSetUseUploadedUserRef(false);
  };

  const completeEnquiryHandler = (continueFromJobId: string) => {
    dispatchGetLastEnquiry();
    refreshShowRecent();
    onClickClearDrawing();
    dispatchClearLodgedEnquiry();
    dispatchClearDuplicateEnquiry();
    dispatchSetUseUploadedUserRef(false);
    setActivateEnquiryForm(false);
    setShowConfirm(false);
    formValuesRefs.current = undefined;
    appliedDraftToForm.current = false;
    if (!continueFromJobId) {
      zoomToFeature(window.homeExtent);
      setJobIdInNotes('');
    } else {
      setRecentVisible(true);
      setJobIdInNotes(`CONTINUE FROM ${continueFromJobId}\n`);
    }
  };

  const closeUploadPanel = () => {
    setShowUpload(false);
    setActiveTool('selector');
  };

  const handleCloseUpload = () => {
    dispatchSetUseUploadedUserRef(false);
    setShowUpload(false);
    setActiveTool('selector');
  };

  const handleConfirmClearMap = () => {
    cancelEnquiryHandler(pendingClearMapFlag.current);
    setShowClearMapWarningModal(false);
    dispatchSetUseUploadedUserRef(false);
  };

  const checkPendingCancelEnquiry = (clear: boolean) => {
    pendingClearMapFlag.current = clear;
    setShowClearMapWarningModal(true);
  };

  const onPointerOverUserRef = (resources: any[]) => {
    let polygonUid = 0;
    let referLayerUid = 0;
    if (canHoverShape.current) {
      resources.forEach((r) => {
        const { graphic } = r;

        if (graphic?.layer?.id === 'drawing-layer') {
          polygonUid = graphic.uid;
        }

        if (graphic?.layer?.id === 'shape-refer-layer') {
          referLayerUid = 1;
        }

        setShowTooltipPolygonUid(polygonUid);
        setShowTooltipReferUid(referLayerUid);
      });
    }
  };

  const onClickMap = (resources: any[]) => {
    let polygonUid = undefined;
    if (shapeDetailFormTouched.current) {
      setShowUnsavedAlert(true);
      return;
    }
    if (canSelectShape.current) {
      resources.forEach((r) => {
        const { graphic } = r;
        if (graphic?.layer?.id === 'drawing-layer') {
          polygonUid = graphic.uid;
          return;
        }
      });
      setSelectedShapeInMap(polygonUid);
    }
  };

  useEffect(() => {
    if (showTooltipPolygonUid !== 0 && showTooltipReferUid !== 0) {
      drawedShapes.forEach((shape) => {
        if (shape.uid === showTooltipPolygonUid) {
          const userRef = shape.userReference || '';
          if (userRef.length > 20) {
            setUserRefTooltipMessage(userRef);
          }
        }
      });
    } else {
      setUserRefTooltipMessage('');
    }
  }, [showTooltipPolygonUid, showTooltipReferUid, drawedShapes]);

  useEffect(() => {
    highlightMapShape(selectedShapeInMap);
    if (selectedShapeInMap === undefined) {
      shapeDetailFormTouched.current = false;
      setShowUnsavedAlert(false);
    }
  }, [selectedShapeInMap]);

  useEffect(() => {
    if (showUnsavedAlert) {
      dispatch(
        addAlert(
          'warning',
          'Unsaved changes',
          'You made changes that have not been applied',
          false,
          0,
          'unsaved-changes'
        )
      );
    } else {
      dispatch(removeAlert('unsaved-changes'));
    }
  }, [showUnsavedAlert, dispatch]);

  useEffect(() => {
    const { loadedSketchViewModel } = window;
    if (loadedSketchViewModel) {
      loadedSketchViewModel.updateOnGraphicClick = !activateEnquiryForm;
    }
  }, [activateEnquiryForm]);

  useEffect(() => {
    if (drawedShapes.length > 0) {
      setActivateEnquiryForm(!multipleDrawMode);
      setShowConfirm(!multipleDrawMode && !lodgedEnquiryOnce.current);
      if (!multipleDrawMode && drawedShapes.length !== shapeAmount.current) {
        closeUploadPanel();
        dispatchGetAssetOwnerCount(
          drawedShapes.length === 1 ? drawedShapes : [],
          projectOrganisationId.current
        );
        if (userDefaultProject.current) {
          dispatchCheckShapeWithinTeam(
            userDefaultProject.current,
            drawedShapes
          );
        }
      }
      const errorShapes: GeoJSON.Geometry[] = [];
      const failedShapes: GeoJSON.Geometry[] = [];
      const referShapes: EnquiryShape[] = [];
      drawedShapes.forEach((s) => {
        if (!s.address) {
          dispatchGetGeocodeStart(s.centroid);
        }
        if (s.isError) {
          errorShapes.push(s.geoJson);
        }
        if (s.isFailedLodge) {
          failedShapes.push(s.geoJson);
        }
        if (s.userReference) {
          referShapes.push(s);
        }
      });
      if (
        (errorShapes.length > 0 || failedShapes.length > 0) &&
        drawedShapes.length > 1 &&
        !multipleDrawMode
      ) {
        errorShapes.length > 0 && addErrorShapes(errorShapes);
        failedShapes.length > 0 &&
          addErrorShapes(failedShapes, 'map-fail-alert');
      } else {
        clearErrorShapes();
      }
      if (
        referShapes.length > 0 &&
        drawedShapes.length > 1 &&
        !multipleDrawMode
      ) {
        addShapesReference(referShapes);
      } else {
        clearShapesReference();
      }
    } else {
      clearErrorShapes();
      clearShapesReference();
      setSelectedShapeInMap(undefined);
    }
    shapeAmount.current = !multipleDrawMode ? drawedShapes.length : 0;
  }, [
    drawedShapes,
    multipleDrawMode,
    dispatchGetGeocodeStart,
    dispatchGetAssetOwnerCount,
    dispatchCheckShapeWithinTeam
  ]);

  useEffect(() => {
    multipleDrawMode && dispatch(removeAlert('outside-team'));
  }, [multipleDrawMode, dispatch]);

  useEffect(() => {
    if (geocodeResponse) {
      if (typeof geocodeResponse.geocode !== 'string') {
        const { geocode, latitude, longitude } = geocodeResponse;
        const address = geocodeToAddress(geocode);
        if (!address.line1) {
          try {
            const plusCode = encodeOpenLocationCode(latitude, longitude);
            address.line1 = `Plus Code ${plusCode}`;
          } catch (e) {
            address.line1 = 'N/A';
          }
        }
        dispatchSetEnquiryAddress(address, longitude, latitude);
      } else {
        dispatchSetEnquiryAddress(
          {
            line1: '',
            line2: '',
            country: 'AUS',
            locality: '',
            postcode: '',
            state: ''
          },
          geocodeResponse.longitude,
          geocodeResponse.latitude
        );
      }
    }
  }, [geocodeResponse, dispatchSetEnquiryAddress]);

  useEffect(() => {
    showRecent(recentVisible);
  }, [recentVisible]);

  useEffect(() => {
    if (user.Project?.organisation_id) {
      projectOrganisationId.current = user.Project.organisation_id;
    }

    if (user.Organisation) {
      if (user.Organisation.has_create_enquiry !== true) {
        setInValidAccess(true);
      }
    } else {
      if (user.is_as_external_user !== true) {
        setInValidAccess(true);
      }
    }

    userDefaultProject.current = user.default_project;
    if (!user.default_project) {
      dispatchResetShapeWithinTeam();
    }

    setIsExternalInPrivate(user.is_as_external_user === true && !user.Project);
  }, [user, dispatchResetShapeWithinTeam]);

  useEffect(() => {
    if (location.state?.duplicateFrom) {
      dispatchGetDuplicateEnquiry(location.state.duplicateFrom);
      history.replace({ state: undefined });
    }
  }, [location, dispatchGetDuplicateEnquiry, history]);

  useEffect(() => {
    mapReady && baseMapTheme && setRecentRenderer(baseMapTheme);
  }, [mapReady, baseMapTheme]);

  useEffect(() => {
    if (shapeWithinTeam) {
      shapeWithinTeam === 'outside' &&
        dispatch(
          addAlert(
            'info',
            `${
              shapeAmount.current > 1 ? 'Some shape(s) are' : 'This shape is'
            } outside of the team boundary`,
            'You can redraw the shape or continue anyway',
            false,
            0,
            'outside-team'
          )
        );
    } else {
      dispatch(removeAlert('outside-team'));
    }
  }, [shapeWithinTeam, dispatch]);

  useEffect(() => {
    if (mapReady) {
      const { loadedMap } = window;
      if (teamShape && showTeamBoundary) {
        addMaskingLayer(
          teamShape,
          addedMaskOnce.current || !window.lastVisitedExtent
        );
      } else {
        const maskingLayer = loadedMap.findLayerById('masking-layer');
        if (maskingLayer) {
          loadedMap.remove(maskingLayer);
        }
      }
      addedMaskOnce.current = true;
    }
  }, [mapReady, teamShape, showTeamBoundary]);

  useEffect(() => {
    mapReady &&
      user &&
      setRecentDefinitionExpression(user.id, teamId, selectedRecentDuration);
    shapeTeamId.current = teamId && teamId > 0 ? teamId : undefined;
  }, [user, teamId, mapReady, selectedRecentDuration]);

  useEffect(() => {
    setShowDraftModal(user?.hasDrafts && draft === undefined);
  }, [user, draft]);

  useEffect(() => {
    if (discardedDraft) {
      dispatch(fetchUserStart({ process: 'refetch' }));
      setShowDraftModal(false);
    }
  }, [discardedDraft, dispatch]);

  const mapActivityToActivityId = (activity: string) => {
    const activityCase = activity.toUpperCase();
    let translation = activityCase;

    if (activityCase === 'PLANNING & DESIGN') {
      translation = 'PLANNING_DESIGN';
    } else if (activityCase === 'HORIZONTAL BORING') {
      translation = 'HORIZ_BORING';
    } else if (activityCase === 'MANUAL EXCAVATION') {
      translation = 'MANUAL_EXCAVATION';
    } else if (activityCase === 'MECHANICAL EXCAVATION') {
      translation = 'MECH_EXCAVATION';
    } else if (activityCase === 'NON-DESTRUCTIVE DIGGING') {
      translation = 'NON_DESTRUCTIVE_DIGGING';
    } else if (activityCase === 'VERTICAL BORING') {
      translation = 'VERTICAL_BORING';
    }

    return translation;
  };

  const mapActivitiesToActivityIds = (activities: string | string[]) => {
    if (Array.isArray(activities)) {
      return activities.map((a) => mapActivityToActivityId(a)).filter(Boolean);
    }
    if (typeof activities === 'string') {
      return activities
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean)
        .map((a) => mapActivityToActivityId(a));
    }
    return [];
  };

  useEffect(() => {
    let newInitialValues = initialValues;
    if (teamDefaultValues) {
      const enquiryLocationsInitialValues: string[] = [];
      if (
        teamDefaultValues.workplaceLocation === 'Both' ||
        teamDefaultValues.workplaceLocation === 'Private property'
      ) {
        enquiryLocationsInitialValues.push('private');
      }

      if (teamDefaultValues.locationInRoad.includes('Road')) {
        enquiryLocationsInitialValues.push('road');
      }

      if (teamDefaultValues.locationInRoad.includes('Nature Strip')) {
        enquiryLocationsInitialValues.push('nature-strip');
      }

      if (teamDefaultValues.locationInRoad.includes('Footpath')) {
        enquiryLocationsInitialValues.push('footpath');
      }
      newInitialValues = {
        ...newInitialValues,
        locations: enquiryLocationsInitialValues,
        enquiryType: teamDefaultValues.enquiryType,
        activityId: mapActivitiesToActivityIds(
          teamDefaultValues?.activity || 'TENDERING'
        ),
        authorityType: teamDefaultValues.workingOnBehalfId,
        authorityId: teamDefaultValues.workingOnBehalfAuthorityId
      };
      setFormInitialValues(newInitialValues);
    }
  }, [teamDefaultValues]);

  useEffect(() => {
    canSelectShape.current =
      pendingEnquiriesAmount === undefined &&
      !lodgingNewEnquiry &&
      enquiries.length <= 0 &&
      !multipleDrawMode &&
      drawedShapes.length > 1;

    canHoverShape.current = !multipleDrawMode && drawedShapes.length > 1;

    if (lodgingNewEnquiry) {
      // Set to true when user click submit
      lodgedEnquiryOnce.current = true;
    }

    if (drawedShapes.length <= 0) {
      // Reset after all shape is clear
      lodgedEnquiryOnce.current = false;
    }
  }, [
    multipleDrawMode,
    drawedShapes,
    pendingEnquiriesAmount,
    lodgingNewEnquiry,
    enquiries
  ]);

  useEffect(() => {
    if (draft?.details?.data) {
      appliedDraftToForm.current = true;
      formValuesRefs.current = draft.details.data;
    }

    if (draft?.shapes && draft.shapes.length > 0) {
      const teamId = draft.shapes[0].project_id;
      addedMaskOnce.current = false;
      userDefaultProject.current = teamId && teamId > 0 ? teamId : undefined;
      dispatch(
        updateUserStart({
          default_project: teamId && teamId > 0 ? teamId : null
        })
      );
      setTimeout(() => {
        dispatch(setTeamBoundaryToggle(true));
      }, 1000);
    }
  }, [draft, dispatch]);

  useEffect(() => {
    if (shapesInitialed.current) {
      if (
        drawedShapes.length === 1 &&
        duplicatedShapeMapId.current === drawedShapes[0].uid
      ) {
        return;
      }
      setShapeChanged(true);
    }
    if (drawedShapes.length > 0) {
      shapesInitialed.current = true;
    }
    dispatch(disableTeamPicker(drawedShapes.length > 0));
  }, [drawedShapes, dispatch]);

  useEffect(() => {
    if (shapeChanged || formChanged) {
      if (authorityTypeList && authorityList && !showDraftModal) {
        const payload = {
          details: {
            ...(formChanged ? formValuesRefs.current : initialValues),
            authorities: {
              authorityType: authorityTypeList,
              authority: authorityList
            }
          },
          teamId: shapeTeamId.current
        };

        if (!lodgedEnquiryOnce.current) {
          debouncedDispatchSaveDraft([payload]);
        }

        setFormChanged(false);
        setShapeChanged(false);
      }
    }
  }, [
    authorityTypeList,
    authorityList,
    shapeChanged,
    formChanged,
    showDraftModal,
    debouncedDispatchSaveDraft
  ]);

  useEffect(() => {
    if (mapReady) {
      setRecentVisible(true);
    }
  }, [mapReady]);

  useEffect(() => {
    dispatchGetRestriction();
  }, [teamId, dispatchGetRestriction]);

  useComponentMount(() => {
    localDispatch(getEnquiryEssentialsStart());
    localDispatch(getLastEnquiryStart());
    return () => {
      dispatch(fetchUserStart({ process: 'refetch' }));
      dispatch(disableTeamPicker(false));
    };
  });

  const appDom = document.getElementById('app');

  if (inValidAccess) {
    return <Redirect to={`${APP_URL.UPGRADE}?preview=create`} />;
  }

  return (
    <LocalStoreContext.Provider value={localStore}>
      <LocalDispatchContext.Provider value={localDispatch}>
        <div
          id="LodgeEnquiry"
          data-testid="lodge-enquiry"
          className={`lodge-enquiry${showConfirm ? ' draw-confirming' : ''}${
            activateEnquiryForm ? ' activate-Form' : ''
          }${multipleDrawMode ? ' multi-draw' : ''}`}
          data-floating-menu-container
        >
          <FullHeaderLeftSideFullPanelLayout
            disableNav={bulkCreating}
            enableHeaderGlobalBar={true}
            mapStyleShowMasking={true}
          >
            {showDraftModal &&
              appDom &&
              ReactDOM.createPortal(
                <ComposedModal
                  className="lodge-enquiry-draft-modal"
                  open={true}
                  preventCloseOnClickOutside={true}
                  size="md"
                >
                  <ModalHeader>
                    <div className="draft-modal-header-label">DRAFTS</div>
                    <div className="draft-modal-header-title">
                      You have draft enquiries
                    </div>
                  </ModalHeader>
                  <ModalBody>
                    One or more shapes on your map are in draft state and have
                    not been lodged. Please choose whether to discard your
                    drafts or continue editing.
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      kind="tertiary"
                      onClick={() => dispatchDiscardDrart()}
                      data-testid="DiscardAllDrafts"
                      disabled={
                        fetchingDraft ||
                        discardingDraft ||
                        assetOwnerNumLoading ||
                        duplicating
                      }
                    >
                      {!discardingDraft ? (
                        'Discard all drafts'
                      ) : (
                        <InlineLoading description="Discarding drafts..." />
                      )}
                    </Button>
                    <Button
                      kind="secondary"
                      data-testid="ContinueEditingDrafts"
                      onClick={() => {
                        setActivateEnquiryForm(false);
                        setShowConfirm(false);
                        dispatchGetDrart();
                      }}
                      disabled={
                        fetchingDraft ||
                        discardingDraft ||
                        assetOwnerNumLoading ||
                        duplicating
                      }
                    >
                      {!fetchingDraft ? (
                        'Review drafts'
                      ) : (
                        <InlineLoading description="Fetching drafts..." />
                      )}
                    </Button>
                  </ModalFooter>
                </ComposedModal>,
                appDom
              )}
            {showClearMapWarningModal &&
              appDom &&
              ReactDOM.createPortal(
                <Modal
                  size="xs"
                  className="clear-map-warning-modal"
                  open={true}
                  onRequestClose={() => setShowClearMapWarningModal(false)}
                  onRequestSubmit={handleConfirmClearMap}
                  primaryButtonText="Yes, proceed"
                  secondaryButtonText="Cancel"
                >
                  All shapes on map will be cleared, do you want to proceed?
                </Modal>,
                appDom
              )}
            <div className="page-heading--wrapper">
              <div className="label">NEW</div>
              <MapTools
                showRemoveTool={activateEnquiryForm || multipleDrawMode}
                disabledRemoveTool={
                  pendingEnquiriesAmount !== undefined ||
                  lodgingNewEnquiry ||
                  enquiries.length > 0 ||
                  duplicating ||
                  (multipleDrawMode && !editingDrawShape)
                }
                disabledUploadTool={multipleDrawMode}
                removeToolLabel={
                  multipleDrawMode && editingDrawShape ? 'Remove' : 'Redraw'
                }
                onRemove={() =>
                  multipleDrawMode
                    ? onDeleteDrawedPolygon()
                    : drawedShapes.length > 1
                    ? checkPendingCancelEnquiry(false)
                    : cancelEnquiryHandler(false)
                }
                activeTool={activeTool}
                onClickTools={(tool: string) =>
                  onClickTools(tool, multipleDrawMode)
                }
                allButtonDisabled={
                  isExternalInPrivate ||
                  !mapReady ||
                  activateEnquiryForm ||
                  geocodeLoading
                }
              />
            </div>
            <MapView
              onClickTools={(tool: string) =>
                onClickTools(tool, multipleDrawMode)
              }
              onClickMap={onClickMap}
              onPointerOverUserRef={onPointerOverUserRef}
              onEdit={(flag: boolean) => setEditingDrawShape(flag)}
              setMapReady={setMapReady}
              activeTool={activeTool}
              showDrawingTooltip={showDrawingTooltip}
              setDuplicatedShapeMapId={setDuplicatedShapeMapId}
              setShowDrawingTooltip={setShowDrawingTooltip}
            />
            <div className="extra-widgets">
              <LiveAreaTag
                activeTool={activeTool}
                isSmallScreen={screenSize === SCREEN_SIZE.SM}
              />
              <div className="show-recents-toggle">
                <ShowRecentsToggle
                  recentVisible={recentVisible}
                  setRecentVisible={setRecentVisible}
                  userId={user.id}
                  selectedRecentDuration={selectedRecentDuration}
                  setSelectedRecentDuration={setSelectedRecentDuration}
                />
              </div>
            </div>
            <UserReferenceTooltip
              userRefTooltipMessage={userRefTooltipMessage}
              view={window.loadedView}
            />
            {showUpload && <EnquiryUploadPanel onClose={handleCloseUpload} />}
            {!activateEnquiryForm && (
              <>
                <div
                  className={`draw-control-instruction${
                    multipleDrawMode ? ' multi-editing-draw' : ''
                  }`}
                >
                  {isExternalInPrivate
                    ? 'Tip: You cannot create enquiry in locate when on private team'
                    : screenSize !== SCREEN_SIZE.MAX &&
                      screenSize !== SCREEN_SIZE.XL
                    ? `Tip: ${getInstruction(activeTool)}`
                    : ''}
                </div>
                {multipleDrawMode && (
                  <div
                    className={`${
                      alertNotificationToggle
                        ? 'noti-alert-notification-finish-draw'
                        : ''
                    } finish-draw-btn-container`}
                  >
                    <Button
                      kind="secondary"
                      disabled={drawedShapes.length === 0}
                      onClick={() => {
                        onClickSelector();
                        dispatchSetMultipleDrawMode(false);
                      }}
                    >
                      Finished drawing
                    </Button>
                  </div>
                )}
              </>
            )}
            {activateEnquiryForm &&
              !(showConfirm && screenSize === SCREEN_SIZE.SM) && (
                <EnquiryFormPanel
                  formInitialValues={formInitialValues}
                  appliedDraftToForm={appliedDraftToForm}
                  formValuesRefs={formValuesRefs}
                  setFormChanged={setFormChanged}
                  jobIdInNotes={jobIdInNotes}
                  selectedShapeInMap={selectedShapeInMap}
                  setSelectedShapeInMap={setSelectedShapeInMap}
                  setShapeDetailFormTouched={(flag: boolean) => {
                    shapeDetailFormTouched.current = flag;
                  }}
                  cancelEnquiryHandler={
                    drawedShapes.length > 1
                      ? checkPendingCancelEnquiry
                      : cancelEnquiryHandler
                  }
                  completeEnquiryHandler={completeEnquiryHandler}
                />
              )}
            {showConfirm && screenSize === SCREEN_SIZE.SM && (
              <ConfirmationPanel
                cancelEnquiryHandler={
                  drawedShapes.length > 1
                    ? checkPendingCancelEnquiry
                    : cancelEnquiryHandler
                }
                confirmDraw={() => setShowConfirm(false)}
                showShapeOutTeamWarning={shapeWithinTeam === 'outside'}
              />
            )}
          </FullHeaderLeftSideFullPanelLayout>
        </div>
      </LocalDispatchContext.Provider>
    </LocalStoreContext.Provider>
  );
};

export default React.memo(LodgeEnquiry);
